/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Heading, isEmpty, Text } from 'bold-ui'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { Fragment, ReactNode, useMemo } from 'react'
import { humanizeAge } from 'util/date/humanize-age'
import { formatNomeCidadao } from 'view/cidadao/util-cidadao'

import { MChatFooterInfo } from '../mchat/components/MChatFooterInfo'
import { MChatAnteriorFooterInfoModel, MChatRiscoPrevioRelevanteInfoModel } from '../mchat/model-mchat'
import { MCHAT_RF_RESULTADO_BY_STATUS, MChatRfResultadoStatus } from '../model-mChatRf'
import { MChatRAnteriorAlert } from './MChatRfAlert'
import { MChatRfResultadoAlert } from './MChatRfResultadoAlert'

interface MChatRfResultadoViewProps {
  status: MChatRfResultadoStatus
  title: string
  label: string
  description: ReactNode
  content?: ReactNode
  footerInfo?: MChatAnteriorFooterInfoModel
  alertaMChatAnterior?: MChatRiscoPrevioRelevanteInfoModel
  deveExibirAlertaRiscoBaixoApos24Meses?: boolean
}

export function MChatRfResultadoView(props: Readonly<MChatRfResultadoViewProps>) {
  const {
    title,
    status,
    label,
    description,
    content,
    footerInfo,
    alertaMChatAnterior,
    deveExibirAlertaRiscoBaixoApos24Meses,
  } = props

  const hasContent = !isEmpty(content)
  const hasFooterInfo = !isEmpty(footerInfo)
  const styles = useMemo(() => createStyles(status), [status])

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento },
    cidadao: { dataNascimento, nome, nomeSocial },
  } = useAtendimentoContext()

  return (
    <Fragment>
      <Heading level={3} fontWeight='normal' style={styles.heading}>
        <strong css={styles.title}>{title}</strong> | <Text fontSize={1}>{label}</Text>
      </Heading>
      {hasContent && <div css={styles.contentWrapper}>{content}</div>}
      {!deveExibirAlertaRiscoBaixoApos24Meses && (
        <MChatRfResultadoAlert status={status} style={styles.alert}>
          {description}
        </MChatRfResultadoAlert>
      )}
      {deveExibirAlertaRiscoBaixoApos24Meses && (
        <div css={styles.alert}>
          <Alert type='warning'>
            <strong>Reavaliação recomendada:</strong> Foi calculado <strong>Risco baixo</strong> anteriormente e é
            recomendado reavaliar após o aniversário de dois anos da criança. {formatNomeCidadao({ nome, nomeSocial })}{' '}
            está com {humanizeAge(dataNascimento, dataAtendimento.getTime())}.
          </Alert>
        </div>
      )}
      {hasFooterInfo && <MChatFooterInfo info={footerInfo} cidadaoDataNascimento={dataNascimento} />}
      {alertaMChatAnterior && <MChatRAnteriorAlert alerta={alertaMChatAnterior} />}
    </Fragment>
  )
}

const createStyles = (status: MChatRfResultadoStatus) => ({
  heading: css`
    margin-bottom: 0.5rem;
  `,
  title: css`
    color: ${MCHAT_RF_RESULTADO_BY_STATUS[status].colorScale.c40};
  `,
  contentWrapper: css`
    padding: 0.5rem 0;
    margin-bottom: 0.5rem;
  `,
  alert: css`
    margin-bottom: 0.5rem;
  `,
})
